import React, { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import Icon, { RightOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { ReactComponent as ShopeeSvgIcon } from 'assets/icons/shopee.svg';
import { useTranslate } from '@tolgee/react';
import { Space } from 'antd';
import { useRequest } from 'ahooks';
import { getChannelInfo } from 'services/api/account';
import { ReactComponent as FreeSvgIcon } from '../assets/free.svg';
import { ReactComponent as OfficialAuthedSvgIcon } from '../assets/official-authed.svg';
import CheckboxGroup from './CheckboxGroup';
import { BottomButton, Footer, Label } from './styles';
import { QuickChatControl } from 'components/QuickChatControl';
import { useTransAppProductName } from 'hooks/useTrans';
import { useTikTokUSEnable } from 'hooks/business/store/useTikTokUSEnable';
import { PlatformIcon } from 'components/PlatformIcon';
import { useAuthorizationPolicy } from '../hooks/useAuthorizationPolicy';
import { authScopePlatformAllowed } from '../utils/authScope';
import { AuthScopeHint } from './AuthScopeHint';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const TitleContainer = styled.div`
  width: 100%;
  background-color: var(--fett-erp-card-bg);
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const SubTitleContainer = styled.div`
  width: 60%;
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const Title = styled.p`
  width: 60%;
  text-align: left;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 8px;
`;
const SubTitleItem = styled(Space)`
  flex: 1;
`;
const SubTitleContent = styled.div`
  flex: 1;
`;
const SubTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: #173164;
  margin-bottom: 4px;
`;
const SubTitleDescription = styled.div`
  font-size: 14px;
  color: #55678c;
  white-space: break-spaces;
  word-wrap: break-word;
`;
const PlatformSelect = styled.div`
  width: 512px;
  margin-top: 20px;
  margin-bottom: 70px;
`;
const PlatformBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
const PlatformText = styled.span`
  font-size: 24px;

  &.shopee {
    color: #eb582b;
  }
`;

const iconCls = css`
  font-size: 48px;
  margin-right: 8px;
`;

export interface PlatformProps {
  platform: General.Platform;
  onPlatformChange: (value: General.Platform) => void;
  onNext?: () => void;
}

const Platform = (props: PlatformProps) => {
  const { platform, onPlatformChange, onNext } = props;
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();

  const tiktokUSEnable = useTikTokUSEnable();
  const { data } = useRequest(getChannelInfo);
  const { policy } = useAuthorizationPolicy();

  const platformRestricted = useCallback(
    (value: General.Platform) => (policy ? !authScopePlatformAllowed(policy, value) : false),
    [policy],
  );

  const platformOptions: Array<{
    value: General.Platform;
    render: React.ReactNode;
    disabled?: boolean;
  }> = useMemo(() => {
    const data: Array<{
      value: General.Platform;
      render: React.ReactNode;
      disabled?: boolean;
    }> = [
      {
        value: 'shopee',
        disabled: platformRestricted('shopee'),
        render: (
          <PlatformBox>
            <Icon className="text-[32px]" component={ShopeeSvgIcon} />
            <PlatformText className="shopee">Shopee</PlatformText>
          </PlatformBox>
        ),
      },
      {
        value: 'lazada',
        disabled: platformRestricted('lazada'),
        render: (
          <PlatformBox>
            <PlatformIcon platform="LAZADA" size={32} />
            <PlatformText>Lazada</PlatformText>
          </PlatformBox>
        ),
      },
      {
        value: 'tiktok',
        disabled: platformRestricted('tiktok'),
        render: (
          <PlatformBox>
            <PlatformIcon platform="TIKTOK" size={32} />
            <PlatformText>TikTok</PlatformText>
          </PlatformBox>
        ),
      },
    ];

    if (tiktokUSEnable) {
      data.push({
        value: 'tiktok_us',
        disabled: platformRestricted('tiktok_us'),
        render: (
          <PlatformBox>
            <PlatformIcon platform="TIKTOK" size={32} />
            <PlatformText>TikTok US</PlatformText>
          </PlatformBox>
        ),
      });
    }

    data.push(
      {
        value: 'mercado',
        disabled: platformRestricted('mercado'),
        render: (
          <PlatformBox>
            <PlatformIcon platform="MERCADO" size={32} />
            <PlatformText>Mercado</PlatformText>
          </PlatformBox>
        ),
      },
      {
        value: 'kwai',
        disabled: platformRestricted('kwai'),
        render: (
          <PlatformBox>
            <PlatformIcon platform="KWAI" size={32} />
            <PlatformText>Kwai</PlatformText>
          </PlatformBox>
        ),
      },
      {
        value: 'facebook',
        disabled: platformRestricted('facebook'),
        render: (
          <PlatformBox>
            <PlatformIcon platform="FACEBOOK" size={32} />
            <PlatformText>Facebook</PlatformText>
          </PlatformBox>
        ),
      },
    );

    return data;
  }, [tiktokUSEnable, platformRestricted]);

  return (
    <Container>
      <TitleContainer>
        <Title>{appProductNameT('authShop.welcomeMessage')}</Title>
        <SubTitleContainer>
          <SubTitleItem align="center" size={16}>
            <Icon className={iconCls} component={OfficialAuthedSvgIcon} />
            <SubTitleContent>
              <SubTitle>{t('authShop.pointOfficialAPI')}</SubTitle>
              <SubTitleDescription>{t('authShop.pointStoreRisk')}</SubTitleDescription>
              <SubTitleDescription>{t('authShop.pointMsgMiss')}</SubTitleDescription>
              <QuickChatControl>
                <SubTitleDescription>{t('authShop.pointIp')}</SubTitleDescription>
              </QuickChatControl>
            </SubTitleContent>
          </SubTitleItem>
          <QuickChatControl>
            <SubTitleItem align="center" size={16}>
              <Icon className={iconCls} component={FreeSvgIcon} />
              <SubTitleContent>
                <SubTitle>
                  {t('authShop.立享days天免费使用', { days: data?.trialDays || 0 })}
                </SubTitle>
                <SubTitleDescription>{t('authShop.现在添加店铺即可立享')}</SubTitleDescription>
              </SubTitleContent>
            </SubTitleItem>
          </QuickChatControl>
        </SubTitleContainer>
      </TitleContainer>
      <PlatformSelect
        style={{
          width: platformOptions.length === 4 ? 812 : 512,
        }}
      >
        <Label>{t('authShop.选择店铺所在平台')}</Label>
        {platformOptions.some((option) => option.disabled) ? <AuthScopeHint /> : null}
        <CheckboxGroup
          value={platform}
          options={platformOptions}
          onChange={(value) => onPlatformChange(value as General.Platform)}
        />
      </PlatformSelect>

      <Footer>
        <BottomButton
          size="large"
          type="primary"
          disabled={platformRestricted(platform)}
          onClick={onNext}
        >
          {t('authShop.下一步')}
          <RightOutlined />
        </BottomButton>
      </Footer>
    </Container>
  );
};

export default Platform;
